// Standard API response envelopes + the canonical user-facing error copy.
// Imported by `apiHandler` in admin-web (and bidder-web in BL-329 phase 3)
// so every error reaches the user with one shape and one message.
//
// See `.claude/rules/error-handling-chain.md` for the bubble + middleware pattern.

/**
 * Standard error response envelope returned by `apiHandler` whenever a route
 * throws. `requestId` is the OTel trace ID propagated through ALS via the
 * `x-request-id` header — quoting it lets ops search the logs directly.
 */
export interface ErrorEnvelope {
  success: false;
  error: string;
  requestId?: string;
  fieldErrors?: Record<string, string>;
}

/** Standard success response envelope. */
export interface SuccessEnvelope<T> {
  success: true;
  data: T;
  /**
   * Partial-failure signal: the primary write succeeded but a secondary op
   * (inventory sync, notification, audit) did not. Per
   * `.claude/rules/error-handling-chain.md` a partial success is
   * `{ success: true, data, warning }` — never a silent `success: true`. The
   * caller surfaces this to the user (toast / banner).
   */
  warning?: string;
}

/**
 * Base copy without the reference-ID tail. Used by client-only boundaries
 * (`global-error.tsx`, `(dashboard)/error.tsx`) where no requestId exists.
 */
export const STANDARD_ERROR_BODY =
  "We're sorry but something went wrong with that. Please try again";

/**
 * Composes the canonical user-facing error message. Drop the
 * "with reference ID …" tail when no requestId is available — pure-client
 * render errors don't have one to surface.
 *
 * @param requestId - The OTel trace ID from ALS (or undefined for client-only
 *   render errors). Surfaced verbatim in the message so operators can grep
 *   the logs by it.
 * @returns The canonical user-facing error string, with or without the
 *   reference-ID tail depending on whether requestId was provided.
 */
export function formatErrorMessage(requestId?: string): string {
  return requestId
    ? `${STANDARD_ERROR_BODY}, or contact support with reference ID ${requestId}.`
    : `${STANDARD_ERROR_BODY}.`;
}
